import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Text } from '@chakra-ui/react';

import { IConversation } from '../../../api/types';
import { ReactComponent as WarningIcon } from '../../../assets/icons/chat/warning.svg';

import styles from './MessageList.module.scss';

interface AgentWarningProps {
    conversation?: IConversation;
    onOpenNewChat: () => void;
}

const INACTIVE_TIMEOUT = 5 * 60 * 1000; // 5 minutes

export const AgentWarning: React.FC<AgentWarningProps> = ({ conversation, onOpenNewChat }) => {
    const appSnapshot = conversation?.app_snapshot;
    const { t } = useTranslation('translations');
    const [isInactive, setIsInactive] = useState(false);

    useEffect(() => {
        const checkIfLastMessageIsInactive = () => {
            const messages = conversation?.messages;
            if (!messages?.length) {
                return false;
            }

            const lastMessage = messages[messages.length - 1];
            const finishedAt = lastMessage?.finished_at;
            if (!finishedAt) {
                return false;
            }

            const lastUpdatedAt = new Date(finishedAt + 'Z').getTime();
            const now = Date.now();
            const diff = now - lastUpdatedAt;

            return diff > INACTIVE_TIMEOUT;
        };

        const updateInactiveState = () => {
            const inactive = checkIfLastMessageIsInactive();
            setIsInactive(inactive);
        };

        const inactive = checkIfLastMessageIsInactive();
        setIsInactive(inactive);

        if (inactive) {
            // We don't need to check if the message is inactive.
            return;
        }

        const interval = setInterval(updateInactiveState, 60 * 1000); // check every 1 minute
        return () => clearInterval(interval);
    }, [conversation?.messages]);

    if (!appSnapshot || !isInactive) {
        return null;
    }

    const { name } = appSnapshot;

    return (
        <Box className={styles.agentWarning}>
            <WarningIcon />
            <Text>{t('chat.agent-conversation-warning', { appName: name })}</Text>
            <Button variant="chat-ask-an-expert" onClick={onOpenNewChat}>
                {t('chat.open-new-chat')}
            </Button>
        </Box>
    );
};
